import React, { useEffect, useRef, useState } from "react";
import { useCart } from "../../site/cart";
import { isBuyable } from "../../site/pages/Products";
import { FwTerms } from "./LaonaFirmware";
import { manualApi, projectApi, won } from "./LaonaProjects";

/* =========================================================
   장바구니에 담기 — 내 프로젝트 카드의 [장바구니에 담기] 팝업 (2026-11, [펌웨어 개발의뢰] · [주문하기]를 대신한다).
   서버(app/laona_manual.py /parts · app/laona_fw.py /parts)가 설계에 든 모듈 · 브레드보드를 세어 주고,
   가격 · 판매 상태는 공개 제품 목록(/api/products)에서 상품코드로 확인한다.
   수량을 고칠 수 있고, 판매 중이며 가격이 있는 부품만 I.META 장바구니(site/cart.jsx)에 바로 담긴다 — 페이지는 그대로 둔다.
   아래 [펌웨어 개발 옵션]에 체크하면 기본 빌드비가 합계에 더해지고 함께 담긴다.
   · 수동 프로젝트 — 고를 수 있다. 체크하고 담으면 그때 기능정의서로 펌웨어 개발의뢰를 만들거나 고친다
     (POST /api/laona/manual/{id}/firmware). 기능정의서가 없으면 고를 수 없다.
     프로젝트 번호도 장바구니에 남겨, 주문서를 내면 주문이 프로젝트에 이어지고 자료 다운로드가 열린다.
   · 라오나봇 프로젝트(kind "bot") — [내 프로젝트로 저장]할 때 만든 의뢰가 체크된 채 고정된다(뺄 수 없다).
   담으면 팝업을 닫고 onDone 을 부른다 — 카드에 '장바구니에 담긴 상품은 … MY 페이지에서' 안내가 뜬다.
   ========================================================= */

function shopState(code, byCode) {
  if (!code) return { k: "none", t: "판매 준비 중", p: null };
  const p = byCode.get(code.toUpperCase());
  if (!p) return { k: "missing", t: "쇼핑몰 미등록", p: null };
  if (p.status === "SOLDOUT") return { k: "soldout", t: "품절", p };
  if (!isBuyable(p)) return { k: "inquiry", t: "가격 문의", p };
  return { k: "ok", t: "담기 가능", p };
}

/* 이미 주문에 들었거나 개발이 시작된 의뢰 — 다시 담지 않는다 */
const FW_BUSY = ["ORDERED", "RECEIVED", "IN_PROGRESS"];

export default function CartDialog({ project, onClose, onDone }) {
  const ref = useRef(null);
  const cart = useCart();
  const bot = project.kind === "bot";
  const [state, setState] = useState({ status: "loading", rows: [], error: "" });
  /* 펌웨어 — fee: 기본 빌드비, spec: 수동 프로젝트의 기능정의서(없으면 ""), req: 최근 의뢰 */
  const [fw, setFw] = useState({ fee: null, spec: null, req: project.fw || null });
  const [fwOn, setFwOn] = useState(bot);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      bot ? projectApi(`/${project.id}/parts`) : manualApi(`/${project.id}/parts`),
      fetch("/api/products").then((r) => (r.ok ? r.json() : { items: [] })).catch(() => ({ items: [] })),
    ])
      .then(([parts, products]) => {
        const byCode = new Map((products.items || []).filter((p) => p.code).map((p) => [p.code.trim().toUpperCase(), p]));
        const rows = parts.items.map((it) => ({ ...it, st: shopState(it.code, byCode) }));
        if (alive) setState({ status: "ready", rows, error: "" });
      })
      .catch((err) => alive && setState({ status: "error", rows: [], error: err.message }));
    return () => { alive = false; };
  }, [project.id, bot]);

  useEffect(() => {
    let alive = true;
    if (bot) {
      fetch("/api/laona/fw/fee", { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => alive && d && setFw((s) => ({ ...s, fee: d.fee })))
        .catch(() => {});
    } else {
      manualApi(`/${project.id}/firmware`)
        .then((d) => alive && setFw({ fee: d.fee, spec: d.func_spec || "", req: d.request }))
        .catch(() => alive && setFw((s) => ({ ...s, spec: "" })));
    }
    return () => { alive = false; };
  }, [project.id, bot]);

  const setQty = (i, v) => setState((s) => ({
    ...s,
    rows: s.rows.map((r, j) => (j === i ? { ...r, qty: Math.max(0, Math.min(99, parseInt(v, 10) || 0)) } : r)),
  }));

  /* 같은 상품코드를 쓰는 부품은 장바구니에서 한 줄로 합친다 */
  const lines = new Map();
  for (const r of state.rows) {
    if (r.st.k !== "ok" || r.qty <= 0) continue;
    const code = r.code.toUpperCase();
    const o = lines.get(code);
    lines.set(code, { p: r.st.p, n: (o ? o.n : 0) + r.qty });
  }
  let sum = 0;
  let cnt = 0;
  for (const v of lines.values()) { sum += v.p.price * v.n; cnt += v.n; }

  /* 펌웨어 개발 옵션을 고를 수 있는지 — 안 되면 까닭 */
  const req = fw.req;
  const fwBusy = req && FW_BUSY.includes(req.status);
  const fwWhy = fwBusy
    ? `펌웨어 개발의뢰 ${req.request_no}는 이미 ${req.status === "ORDERED" ? `주문 #${req.order_no}에 들어 있습니다` : "개발이 진행 중입니다"}.`
    : bot
      ? (req && req.status === "REQUESTED" ? "" : "펌웨어 개발의뢰를 찾을 수 없습니다. 문의해 주세요.")
      : fw.spec === null ? "불러오는 중…"
        : !fw.spec ? "라오나 에디터에서 [기능정의]를 작성하고 [완료]를 누르면 고를 수 있습니다." : "";
  const fwSel = !fwWhy && (bot || fwOn);
  const fwInCart = req && cart.fw.some((r) => r.id === req.id);
  const total = sum + (fwSel ? fw.fee || 0 : 0);
  const canAdd = !busy && (lines.size > 0 || fwSel) && !(fwSel && fw.fee == null);

  async function addToCart() {
    setBusy(true);
    setError("");
    try {
      if (fwSel) {
        /* 수동 프로젝트는 지금 기능정의서로 의뢰를 만들거나 고친다 */
        const r = bot ? req : await manualApi(`/${project.id}/firmware`, { method: "POST" });
        cart.addFw({ id: r.id, no: r.request_no, title: r.title, fee: fw.fee, locked: bot });
      }
      for (const v of lines.values()) cart.add(v.p, Math.min(99, v.n));
      if (!bot && lines.size) cart.addProject(project.id); // 주문이 프로젝트에 이어진다 — 자료 다운로드
      await onDone?.();
      ref.current?.close();
    } catch (err) {
      setError(err.message || "장바구니에 담지 못했습니다.");
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="lb-dialog lb-dialog--order"
      aria-labelledby="lb-order-title"
      onClose={onClose}
      onClick={(e) => { if (e.target === ref.current && !busy) ref.current.close(); }}
    >
      <div className="lb-dialog__box">
        <div className="lb-dialog__head">
          <h2 id="lb-order-title">장바구니에 담기</h2>
          <button type="button" className="lb-dialog__x" aria-label="닫기" onClick={() => ref.current.close()} disabled={busy}>×</button>
        </div>
        <p className="lb-dialog__lead">
          <b>{project.title}</b> — {bot ? "라오나봇 설계의 모듈" : "설계에 포함된 모듈"}과 브레드보드입니다. 수량을 고칠 수 있고, ‘담기 가능’인 부품만 장바구니에 담깁니다.
          {!bot && " 주문을 마치면 이 프로젝트의 자료 다운로드(결선도 · 배선도 · 전체 BOM)가 열립니다."}
        </p>

        {state.status === "loading" && <p className="lb-empty">부품 목록을 불러오는 중입니다…</p>}
        {state.status === "error" && <p className="lb-error">{state.error || "부품 목록을 불러오지 못했습니다."}</p>}
        {state.status === "ready" && !state.rows.length && (
          <p className="lb-empty">설계에 놓인 모듈이 없습니다. 프로젝트를 열어 모듈을 배치해 주세요.</p>
        )}
        {state.status === "ready" && state.rows.length > 0 && (
          <>
            <div className="lb-order__wrap">
              <table className="lb-order">
                <thead>
                  <tr><th>부품</th><th>수량</th><th>쇼핑몰 상품</th><th className="num">단가</th><th className="num">금액</th><th>상태</th></tr>
                </thead>
                <tbody>
                  {state.rows.map((r, i) => (
                    <tr key={`${r.kind}:${r.id}`} className={`st-${r.st.k}`}>
                      <td>
                        <span className="lb-order__mod">
                          {r.image ? <img alt="" src={r.image} /> : <span className="lb-order__noimg" aria-hidden="true" />}
                          <span><b>{r.id}</b>{r.kind === "breadboard" && <small>브레드보드</small>}</span>
                        </span>
                      </td>
                      <td>
                        <input className="lb-order__qty" type="number" min="0" max="99" value={r.qty} disabled={busy}
                               aria-label={`${r.id} 수량`} onChange={(e) => setQty(i, e.target.value)} />
                      </td>
                      <td className="lb-order__pname">{r.st.p ? r.st.p.name : "—"}</td>
                      <td className="num">{r.st.p && r.st.p.price != null ? won(r.st.p.price) : "—"}</td>
                      <td className="num">{r.st.k === "ok" ? won(r.st.p.price * r.qty) : "—"}</td>
                      <td><span className={`lb-order__st lb-order__st--${r.st.k}`}>{r.st.t}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {state.rows.some((r) => r.st.k !== "ok") && (
              <p className="lb-order__note">‘판매 준비 중’ · ‘쇼핑몰 미등록’ · ‘품절’ · ‘가격 문의’ 부품은 담기지 않습니다.</p>
            )}
          </>
        )}

        <div className="lb-fw__order">
          <label className="lb-check">
            <input type="checkbox" checked={fwSel} disabled={bot || !!fwWhy || busy} onChange={(e) => setFwOn(e.target.checked)} />
            <span>
              <b>펌웨어 개발 옵션</b> (+{fw.fee == null ? "…" : won(fw.fee)}) — 기능정의서대로 펌웨어를 개발 · 빌드합니다
            </span>
          </label>
          {fwWhy && fwWhy !== "불러오는 중…" && <span className="lb-order__note">{fwWhy}</span>}
          {bot && !fwWhy && <span className="lb-order__note">라오나봇 프로젝트는 펌웨어 개발 · 빌드를 빼고 담을 수 없습니다.</span>}
          {fwInCart && !fwBusy && <span className="lb-order__note">이 프로젝트의 펌웨어 개발 옵션은 이미 장바구니에 있습니다 — 담으면 한 건으로 합쳐집니다.</span>}
          {fwSel && fw.fee != null && (
            <>
              <FwTerms fee={fw.fee} />
              {!bot && fw.spec && (
                <details className="lb-fw__spec">
                  <summary className="lb-fw__spech">기능정의서 보기</summary>
                  <pre>{fw.spec}</pre>
                </details>
              )}
            </>
          )}
        </div>

        <div className="lb-order__sum">
          <span>부품 <b>{lines.size}종 {cnt}개</b> {won(sum)}</span>
          {fwSel && <span>펌웨어 개발 <b>{fw.fee == null ? "…" : won(fw.fee)}</b></span>}
          <span>합계 <b>{won(total)}</b></span>
          <span className="lb-order__note">배송비는 장바구니에서 계산됩니다.</span>
        </div>

        {error && <p className="lb-error">{error}</p>}
        <div className="lb-order__acts">
          <button type="button" className="lb-btn" onClick={() => ref.current.close()} disabled={busy}>닫기</button>
          <button type="button" className="lb-btn lb-btn--primary" disabled={!canAdd} onClick={addToCart}>
            {busy ? "담는 중…" : "장바구니에 담기"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
